import { createModelSelector, createSelector } from '@ngxs/store';
import { MatterClientIntakeFormState } from '@state/matter/client-intake/client-intake.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { PortalInviteState } from '@state/portal-invite/portal-invite.state';
import {
	IntakeDropdownViewModel,
	intakeDropdownViewModel,
	IntakeFormStatusDropdownOptions,
	IntakeHeaderViewModel,
	intakeHeaderViewModel,
	intakePageViewModel,
	IntakePageViewModel,
	ShareIntakeFormModalViewModel,
	IntakeStatusDisplayViewModel,
	intakeStatusDisplayViewModel,
} from './models';
import { MatterClients } from 'src/app/shared/state/global/matter_clients';
import { getFullName } from '@globalUtils/utilities';
import { BusinessState } from 'src/app/shared/state/business';
import {
	ClientIntakeForm,
	ClientIntakeFormContext,
	ClientIntakeFormType,
	IntakeFormContextFsDocType,
	IntakeFormStatus,
} from '@models/client-intake';
import { BusinessUsersState } from 'src/app/shared/state/business-users/business-users.state';
import { PortalInvitee, PortalInviteStatus } from '@models/portal-invites';
import { nanoid } from 'nanoid';
import { clientsWithAssociations } from '@globalUtils/clients/clients';
import {
	PersonMatterClientWithAssociations,
	MatterFileType,
} from '@models/matter';
import { ClientType } from '@models/clients';

export const $intakeFormStatusDropdownOptions = createSelector(
	[MatterClientIntakeFormState.getMostRecentActiveForm],
	(form) => {
		return (
			IntakeFormStatusDropdownOptions.find(
				(option) => option.id === form?.status,
			) || null
		);
	},
);

export const $clientsWithAssociations = createSelector(
	[
		MatterClients.get_personMatterClientsWithAssociationMapped,
		MatterClients.get_matterClientsWithAssociationMapped,
	],
	(clients, associationClients): PersonMatterClientWithAssociations[] => {
		return clientsWithAssociations(clients, associationClients);
	},
);

export const $intakeHeaderViewModel = createSelector(
	[
		MatterClientIntakeFormState.getMostRecentActiveForm,
		$intakeFormStatusDropdownOptions,
		SelectedMatter.id,
		PortalInviteState.getInviteByPortalServiceId,
		BusinessUsersState.getLoggedInBusinessUser,
	],
	(
		intakeForm,
		dropdownItem,
		matterId,
		getInviteByPortalServiceId,
		loggedInUser,
	): IntakeHeaderViewModel => {
		const portalInvite = intakeForm?.id
			? getInviteByPortalServiceId(intakeForm.id)
			: null;

		const baseViewModel = {
			dropdownItem,
			matterId,
			intakeForm,
			portalInvite,
			loggedInUser,
			inviteStatus: {
				isDraft: portalInvite?.status === PortalInviteStatus.DRAFT,
				isActive: portalInvite?.status === PortalInviteStatus.ACTIVE,
			},
			inviteContainsInvitees:
				portalInvite?.invitees && portalInvite.invitees.length > 0,
		};

		if (portalInvite?.status === PortalInviteStatus.ACTIVE) {
			return {
				...baseViewModel,
				primaryAction: { label: 'View Responses', icon: '' },
				backLabel: 'Back',
				title: 'Client Intake Request',
			};
		}

		const returnObject = {
			...intakeHeaderViewModel,
			...baseViewModel,
		};

		return returnObject;
	},
);

export const $shareIntakeFormModalViewModel = createSelector(
	[
		MatterClientIntakeFormState.getMostRecentActiveForm,
		$clientsWithAssociations,
		PortalInviteState.getInviteByPortalServiceId,
	],
	(
		intakeForm,
		clients,
		getInviteByPortalServiceId,
	): ShareIntakeFormModalViewModel => {
		const portalInvite = intakeForm?.id
			? getInviteByPortalServiceId(intakeForm.id)
			: null;

		const existingClientIds =
			portalInvite?.invitees?.map((invitee) => invitee.clientId) ?? [];

		const clientDropdownItems = clients
			.filter((client) => !existingClientIds.includes(client.id))
			.map((client) => {
				const fullName = getFullName([
					client?.firstName,
					client?.middleNames,
					client?.lastName,
				]);
				return {
					name: fullName,
					value: {
						email: client?.email ?? '',
						sentHistory: [],
						clientId: client.id,
						name: fullName,
						displayName: fullName,
						isActive: true,
						inviteAccepted: false,
					},
				};
			});

		let sortedInvitees: PortalInvitee[] = [];
		const invitees = portalInvite?.invitees;

		if (invitees && invitees.length > 0) {
			const inviteesClone = structuredClone(invitees);

			sortedInvitees = inviteesClone.sort((a, b) => {
				return a.name.localeCompare(b.name);
			});
		}

		return {
			clientDropdownItems,
			intakeForm,
			portalInvite,
			sortedInvitees,
		};
	},
);

export const DETAILS_NAME_MAP = {
	[MatterFileType.PURCHASE]: 'Purchase Details',
	[MatterFileType.SALE]: 'Sale Details',
	[MatterFileType.REFINANCE]: 'Refinance Details',
} as const;

export const $intakeFormContextData = createSelector(
	[
		SelectedMatter.getSelected,
		MatterClients.get_matterClientsWithAssociationMapped,
	],
	(matter, allClients): ClientIntakeFormContext => {
		return {
			clients: {
				all: allClients.map((client) => ({
					id: client.id,
					name:
						client.type === ClientType.PERSON
							? getFullName([
									client.firstName,
									client.middleNames,
									client.lastName,
								])
							: client.name,
					type: {
						person: client.type === ClientType.PERSON,
						corporation: client.type === ClientType.CORPORATION,
						builder: client.type === ClientType.BUILDER,
					},
				})),
			},
			general: {
				id: IntakeFormContextFsDocType.ON_MATTER_UPDATE,
				name: DETAILS_NAME_MAP[matter?.fileType],
				type: ClientIntakeFormType.GENERAL,
				hasPersonClients: allClients.some(
					(client) => client.type === ClientType.PERSON,
				),
				hasCorporationClients: allClients.some(
					(client) => client.type === ClientType.CORPORATION,
				),
			},
		};
	},
);

export const $intakeFormGeneralData = createSelector(
	[
		SelectedMatter.id,
		BusinessState.businessId,
		BusinessState.businessName,
		BusinessUsersState.getLoggedInBusinessUser,
		SelectedMatter.getIntakeFormRuleDefinition,
	],
	(
		matterId,
		businessId,
		businessName,
		loggedInUser,
		intakeFormRuleDefinition,
	): ClientIntakeForm => {
		return {
			id: nanoid(36),
			matterId: matterId,
			businessId: businessId,
			businessName: businessName,
			title: 'Client Intake Form',
			createdBy: loggedInUser?.id,
			createdAt: Date.now(),
			status: IntakeFormStatus.DRAFT,
			statusHistory: [
				{
					status: IntakeFormStatus.DRAFT,
					updatedBy: loggedInUser?.id,
					updatedAt: Date.now(),
				},
			],
			ruleDefinition: intakeFormRuleDefinition,
		};
	},
);

export const $intakeFormCreationData = createSelector(
	[$intakeFormGeneralData],
	(generalData): ClientIntakeForm => {
		return {
			...generalData,
		};
	},
);

export const $intakeDropdownViewModel = createSelector(
	[
		MatterClientIntakeFormState.getMostRecentActiveForm,
		$intakeFormStatusDropdownOptions,
		SelectedMatter.id,
	],
	(form, dropdownItem, matterId): IntakeDropdownViewModel => {
		return {
			...intakeDropdownViewModel,
			intakeForm: form,
			dropdownItem: dropdownItem,
			matterId,
		};
	},
);

export const $intakePageViewModel = createSelector(
	[
		MatterClientIntakeFormState.getMostRecentActiveForm,
		PortalInviteState.getInviteByPortalServiceId,
	],
	(form, portalInvite): IntakePageViewModel => {
		return {
			...intakePageViewModel,
			intakeForm: form,
			portalInvite: portalInvite(form?.id),
		};
	},
);

export const $clientIntakePageViewModels = createModelSelector({
	$intakeHeaderViewModel: $intakeHeaderViewModel,
	$intakePageViewModel: $intakePageViewModel,
	$intakeFormCreationData: $intakeFormCreationData,
});

export const $intakeStatusDisplayViewModel = createSelector(
	[MatterClientIntakeFormState.getMostRecentActiveForm],
	(intakeForm): IntakeStatusDisplayViewModel => {
		const status = intakeForm?.status;
		const statusOption = IntakeFormStatusDropdownOptions.find(
			(option) => option.id === status,
		);
		const intakeStatusDisplay = statusOption ? statusOption.title : null;

		return {
			...intakeStatusDisplayViewModel,
			intakeStatusDisplay,
		};
	},
);
